import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../lib/useSubscription';

const ProtectedRoute = ({ children, requireClinic = false }) => {
  const { user, clinic, loading, error } = useAuth();
  const location = useLocation();
  const [timeoutReached, setTimeoutReached] = React.useState(false);

  const { subscription, status, loading: subLoading } = useSubscription(clinic?.id);

  // Add a timeout fallback - more aggressive
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        console.warn('ProtectedRoute timeout reached - forcing render');
        setTimeoutReached(true);
      }
    }, 8000); // 8 seconds timeout

    return () => clearTimeout(timer);
  }, [loading]);

  // Show loading spinner while checking auth (unless timeout reached)
  if (loading && !timeoutReached) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#1A202C'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div style={{
            width: '3rem',
            height: '3rem',
            border: '3px solid #4a5568',
            borderTop: '3px solid #10B981',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#f7fafc', fontSize: '1rem' }}>
            Φόρτωση...
          </p>
          <p style={{ color: '#9CA3AF', fontSize: '0.875rem' }}>
            {error ? `Σφάλμα: ${error}` : 'Ελέγχος αυθεντικοποίησης...'}
          </p>
          <button
            onClick={() => setTimeoutReached(true)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#4B5563',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer'
            }}
          >
            Προχωρήστε χωρίς αυθεντικοποίηση
          </button>
        </div>
      </div>
    );
  }

  // If timeout reached, show error or redirect
  if (timeoutReached) {
    console.error('ProtectedRoute: Timeout reached, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Subscription expiry check: redirect to /subscription if expired
  // Don't block while subscription is loading, and don't block the subscription page itself
  if (!subLoading && subscription && status && location.pathname !== '/subscription') {
    const isExpiredOrCancelled = ['cancelled', 'expired'].includes(status.name);
    if (isExpiredOrCancelled) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endDate = subscription.end_date ? new Date(subscription.end_date) : null;
      const trialEnd = subscription.trial_end_date ? new Date(subscription.trial_end_date) : null;
      const pastEndDate = !endDate || today > endDate;
      const pastTrialEnd = !trialEnd || today > trialEnd;

      if (pastEndDate && pastTrialEnd) {
        return <Navigate to="/subscription" replace />;
      }
    }
  }

  // Render the protected content
  return children;
};

export default ProtectedRoute;
